import { AccessLevel } from "@/enums/AccessLevel";
import { ParamField } from "../enums/ParamField";
import { AccessLevelName } from "@/types/AccessLevelName";
import { ModbusParam } from "@/types/ModbusParam";

const LEVELS = new RegExp("[0-9]+");

/**
 * This composable extracts the minimum access level that is required before a parameter field
 * becomes "readable" or "writeable".
 * To this end, a parameter file (csv) may contain a field "write access" and EITHER "read-only"
 * OR "read access".  Using both "read-only" and "read access" at the same time is not supported.
 */

export function useAccess() {
  function extractAccessLevel(
    accessLevelName: AccessLevelName | string,
  ): number {
    const accessMatches: string[] | null = accessLevelName.match(LEVELS);
    return accessMatches
      ? parseInt(accessMatches[0])
      : AccessLevel.unmodifiable;
  }

  /**
   * Under the v0 and v1 parameter lists, a parameter is readable if it is writeable.
   * (the current accesslevel >= the param["write access"] level).
   * Under the v2 parameter list, a parameter has its own "read access" property.
   * (the current accesslevel >= the param["read access"] level).
   */
  function getParamReadAccessLevel(param: ModbusParam): number {
    return ParamField.readAccess in param
      ? extractAccessLevel(param[ParamField.readAccess])
      : extractAccessLevel(param[ParamField.writeAccess]);
  }

  /**
   * Under the v2 parameter list, a parameter has its own "write access" property.
   * (the current accesslevel >= the param["write access"] level).
   * Under the v0 and v1 parameter lists, this depends also on the "read-only" property.
   */
  function getParamWriteAccessLevel(param: ModbusParam): number {
    return ParamField.readOnly in param && param[ParamField.readOnly] === "true"
      ? AccessLevel.manufacturer
      : extractAccessLevel(param[ParamField.writeAccess]);
  }

  return {
    extractAccessLevel,
    getParamReadAccessLevel,
    getParamWriteAccessLevel,
  };
}
